import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@resolution-processing/balance';
import styles from './FileInput.module.css';

interface FileInputProps {
  onSubmit: (contactId: string) => void;
  onClear?: () => void;
  loading: boolean;
  clearable?: boolean;
  initialValue?: string;
  /** Fires as the user types: the resolved contact id and whether it was normalized from a URL/prefix. */
  onContactIdChange?: (info: { contactId: string; normalized: boolean }) => void;
  /** Bumping this pulls focus back to the input (used on Start Over to restart the loop). */
  focusSignal?: number;
}

function extractContactId(input: string): string {
  const trimmed = input.trim();
  // Handle Forth contact URLs. The contact id lives in a different query param
  // depending on which page the URL was copied from:
  //   view page:   ...?module=contacts&page=view2&cid=1239735578&t=Thistory
  //   add/edit:    ...?module=contacts&page=add&edit=1&id=1239781632&fid=30900
  // Read the params by name so we pick the right one and never grab `fid`.
  try {
    const { searchParams } = new URL(trimmed);
    const fromUrl = searchParams.get('cid') ?? searchParams.get('id');
    if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;
  } catch {
    // Not a URL, treat as raw contact ID
  }
  // Strip CRM prefix like "REPR-1211263861" → "1211263861"
  const prefixMatch = trimmed.match(/^[A-Za-z]+-(\d+)$/);
  if (prefixMatch) return prefixMatch[1];
  return trimmed;
}

export function FileInput({ onSubmit, onClear, loading, clearable, initialValue, onContactIdChange, focusSignal }: FileInputProps) {
  const { t } = useTranslation('ui');
  const [value, setValue] = useState(initialValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const resolved = extractContactId(value);

  useEffect(() => {
    const normalized = value.trim().length > 0 && resolved !== value.trim();
    onContactIdChange?.({ contactId: resolved, normalized });
  }, [resolved, value, onContactIdChange]);

  // Focus the input when the parent bumps focusSignal (Start Over → ready for the next run).
  useEffect(() => {
    if (focusSignal) inputRef.current?.focus();
  }, [focusSignal]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (resolved) {
      setValue(resolved);
      onSubmit(resolved);
    }
  };

  const handleClear = () => {
    setValue('');
    onClear?.();
  };

  return (
    <div className={styles.wrapper}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t('input.placeholder')}
          disabled={loading}
        />
        <Button
          variant="primary"
          type="submit"
          className={styles.button}
          disabled={loading || !value.trim()}
        >
          {loading ? t('input.loading') : t('input.submit')}
        </Button>
        {clearable && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            disabled={loading}
          >
            {t('input.clear')}
          </button>
        )}
      </form>
    </div>
  );
}
